import {
  BoundDateField,
  Button,
  Css,
  DateFieldProps,
  LoadingSkeleton,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useModal,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useCallback } from "react";
import { ScheduleSettingDetailsFragment } from "src/fragments/circularFragments.generated";
import { Maybe } from "src/generated/graphqlTypes";
import { useDelayFlagModal } from "src/hooks/useDelayFlagModal";
import { useSaveScheduleTasksMutation } from "src/routes/projects/schedule-v2/ProjectSchedulePageV2.generated";
import { useSetDateViaPredecessorModalQuery } from "src/routes/projects/schedule-v2/table/SetDateViaPredecessor.generated";
import { pluralize } from "src/utils";
import { ObjectConfig, useFormState } from "src/utils/formState";
import { taskDependencyTypeToAbbreviationMapper } from "src/utils/mappers";
import { Temporal } from "temporal-polyfill";
import { calcDependencyLag } from "../calcDependencyLag";
import { DateCellFieldProps } from "./DateCellField";

type SetStartDateViaPredecessorInputProps = Pick<
  DateCellFieldProps,
  "label" | "iconLeft" | "defaultOpen" | "hideCalendarIcon"
> & {
  taskId: string;
  initialValue?: Temporal.PlainDate;
  disabled: React.ReactNode;
  disabledDays: DateFieldProps["disabledDays"];
  scheduleSetting: Maybe<ScheduleSettingDetailsFragment>;
};

/**  Bypass autosave input and launch modal to calculate predecessor lag instead */
export function SetStartDateViaPredecessorInput({
  taskId,
  initialValue,
  defaultOpen,
  iconLeft,
  hideCalendarIcon,
  label,
  disabled,
  disabledDays,
  scheduleSetting,
}: SetStartDateViaPredecessorInputProps) {
  const { openModal } = useModal();

  const formState = useFormState({
    config: formConfig,
    init: { input: initialValue, map: (initialValue) => ({ targetStartDate: initialValue }) },
  });

  const onDateChange = useCallback(
    (date: Temporal.PlainDate | undefined) => {
      if (!date) return;
      const targetStartDate = date;

      // Since we're overriding the default `onChange` behavior to open the modal,
      // we still want to keep the form state in sync to be able to revert
      formState.targetStartDate.set(targetStartDate);
      openModal({
        content: (
          <SetDateViaPredecessorModal
            taskId={taskId}
            targetStartDate={targetStartDate}
            scheduleSetting={scheduleSetting}
            formState={formState}
          />
        ),
        onClose: () => {
          // Resetting the form on modal close accounts for when a user exits the modal without submitting a change,
          // we can revert back to the initial value. If a user does submit, the form will have been updated with the correct value anyway.
          formState.revertChanges();
        },
      });
    },
    [taskId, openModal, scheduleSetting, formState],
  );

  return (
    <BoundDateField
      field={formState.targetStartDate}
      label={label}
      iconLeft={iconLeft}
      disabled={disabled}
      onChange={onDateChange}
      disabledDays={disabledDays}
      defaultOpen={defaultOpen}
      hideCalendarIcon={hideCalendarIcon}
    />
  );
}

type SetDateViaPredecessorModalProps = {
  taskId: string;
  targetStartDate: Temporal.PlainDate;
  scheduleSetting: Maybe<ScheduleSettingDetailsFragment>;
  formState: ObjectState<{ targetStartDate?: Temporal.PlainDate | null | undefined }>;
};

export function SetDateViaPredecessorModal({
  taskId,
  targetStartDate,
  scheduleSetting,
  formState,
}: SetDateViaPredecessorModalProps) {
  const { data } = useSetDateViaPredecessorModalQuery({ variables: { taskId } });
  const { closeModal } = useModal();
  const { openDelayFlagModal } = useDelayFlagModal();
  const [saveScheduleTasks, { loading: saveTaskLoading }] = useSaveScheduleTasksMutation();

  const newDependencyLag = data
    ? calcDependencyLag({ newStartDate: targetStartDate, originalTaskData: data.scheduleTask, scheduleSetting })
    : undefined;

  const onSubmit = useCallback(async () => {
    if (!newDependencyLag) return null;

    const { data } = await saveScheduleTasks({
      variables: { input: { tasks: [{ id: taskId, predecessorDependencies: newDependencyLag.mutationInput }] } },
    });
    formState.commitChanges();

    closeModal();

    const resultingDelay = data?.saveScheduleTasks.resultingDelays[0];
    if (resultingDelay) {
      openDelayFlagModal(resultingDelay);
    }
  }, [newDependencyLag, taskId, saveScheduleTasks, openDelayFlagModal, closeModal, formState]);

  const dayPill = (days: number) => (
    <span css={Css.px1.bgWhite.br24.xsSb.pyPx(2).$}>
      {days} {pluralize(days, "day")}
    </span>
  );

  const predecessorListItems = () => {
    if (!newDependencyLag || saveTaskLoading) return <LoadingSkeleton rows={2} size="lg" />;

    return newDependencyLag.changes.map(({ lagInDays, predecessorId, predecessorName, type, originalLagInDays }) => (
      <div key={predecessorId} css={Css.dg.gtc("3fr 35px 2.5fr").aic.bgGray100.p2.mb2.br8.xs.$}>
        <div data-testid="predecessorName">{predecessorName}</div>
        <div data-testid="taskDependencyType" css={Css.tac.$}>
          {taskDependencyTypeToAbbreviationMapper[type]}
        </div>
        <div css={Css.tac.$} data-testid="lagInDaysDelta">
          {dayPill(originalLagInDays)}
          <span css={Css.md.px1.$}>→</span>
          {dayPill(lagInDays)}
        </div>
      </div>
    ));
  };

  return (
    <>
      <ModalHeader>Confirm date change</ModalHeader>
      <ModalBody>
        <p css={Css.gray700.$}>
          Changing the Start date of this task will result in the following changes to dependency lag:
        </p>
        <p css={Css.smSb.py2.$}>Predecessors:</p>
        {predecessorListItems()}
      </ModalBody>
      <ModalFooter>
        <Button variant="quaternary" label="Cancel" onClick={closeModal} />
        <Button label="Continue" onClick={onSubmit} disabled={!newDependencyLag || saveTaskLoading} />
      </ModalFooter>
    </>
  );
}

const formConfig: ObjectConfig<{ targetStartDate?: Temporal.PlainDate | null }> = {
  targetStartDate: { type: "value" },
};
